<script setup lang="ts">
/**
 * MovementNoticeDialog
 *
 * Kurzer Hinweis-Dialog, der einmalig nach dem Upload eines **mehrteiligen** PDFs erscheint,
 * damit der Nutzer die Movement-Tab-Leiste (in der Vorschau) nicht übersieht. Das Signal
 * (Anzahl der Teile) kommt aus ``useMusicUploadStore.multiPartNotice``; Schließen leert es
 * über ``dismissMultiPartNotice``.
 */
import {computed, onBeforeUnmount, ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {Icon} from '@iconify/vue';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';

const uploadStore = useMusicUploadStore();
const {multiPartNotice} = storeToRefs(uploadStore);

const partCount = computed(() => multiPartNotice.value ?? 0);
const isOpen = computed(() => multiPartNotice.value !== null);

function close(): void {
  uploadStore.dismissMultiPartNotice();
}

// ESC schließt den Dialog (globaler Listener nur bei geöffnetem Dialog).
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}
watch(isOpen, (open) => {
  if (open) document.addEventListener('keydown', onKeydown);
  else document.removeEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const dialogRef = ref<HTMLElement | null>(null);
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
  >
    <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
      <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          class="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl outline-none"
      >
        <header class="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
          <Icon icon="fa6-solid:layer-group" class="shrink-0 text-orange-500" />
          <h2 class="text-base font-semibold text-gray-900">Mehrteiliges Stück erkannt</h2>
        </header>

        <div class="px-5 py-4 text-sm text-gray-700">
          <p>
            Dieses PDF enthält <strong>{{ partCount }} Teile</strong>. Sie werden getrennt
            angezeigt – wechsle die Teile über die
            <strong class="text-orange-600">Tab-Leiste</strong> oberhalb der Notenvorschau.
          </p>
          <p class="mt-2 text-gray-500">
            Jeder Teil lässt sich einzeln transponieren, anpassen und konvertieren.
          </p>
        </div>

        <footer class="flex justify-end border-t border-gray-200 px-5 py-3">
          <button type="button" class="btn btn-primary" @click="close">
            <Icon icon="fa6-solid:check" class="mr-1.5 text-sm" /> Verstanden
          </button>
        </footer>
      </div>
    </div>
  </Transition>
</template>
